/**
 * Hero échéance cave — card sombre en haut du Dashboard.
 *
 * Branché sur `planCave.statsResume` (Lot D), queryKey dédupée avec
 * WidgetStockVivant → aucun round-trip supplémentaire (cache react-query).
 *
 * Affiche la plus proche des deux échéances AOC réelles :
 *   - prochain dégorgement (12 mois minimum sur lattes) ;
 *   - prochaine mise en marché (15 mois BSA · 36 mois millésimé).
 * Complété d'un rappel des lots DÉJÀ mûrs (compteurs `nb_lots_degorgeables` /
 * `nb_lots_expediables`).
 *
 * L'ancien bloc réglementaire (registre / DRM / étiquetage / CRD) a été retiré :
 * aucun module réglementaire n'est encore livré, donc pas de fausse donnée.
 */

import type { EcheanceCave, StatsResumeCaveSortie } from "@champ-erp/contracts";
import { Card } from "@champ-erp/ui";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, CalendarClock, Wine } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { formatDateFr } from "../../lib/format-dates.js";
import { trpc } from "../../lib/trpc.js";

// Type d'échéance affichée : dégorgement (12 mois) ou mise en marché (15/36).
type TypeEcheance = "degorgement" | "mise_en_marche";

// Libellé du badge + verbe de disponibilité + rappel de la règle AOC, par type.
// Sépare la formulation métier du rendu (aucune règle en dur dans le JSX).
const ECHEANCE_CONFIG: Record<TypeEcheance, { badge: string; verbe: string; regle: string }> = {
  degorgement: {
    badge: "Prochain dégorgement",
    verbe: "dégorgeable",
    // 12 mois SUR LIES (pas « sur lattes » : un lot sur pointes compte aussi).
    // On rappelle que dégorger n'est pas commercialiser (échéance plus tardive).
    regle: "12 mois minimum sur lies avant dégorgement (AOC) — la mise en marché vient plus tard.",
  },
  mise_en_marche: {
    badge: "Prochaine mise en marché",
    verbe: "commercialisable",
    regle: "15 mois (BSA) · 36 mois (millésimé) — règle AOC.",
  },
};

export function HeroEcheance() {
  // queryKey identique à WidgetStockVivant → une seule requête réseau partagée.
  const { data, isLoading, isError } = useQuery({
    queryKey: ["planCave.statsResume"],
    queryFn: () => trpc.planCave.statsResume.query(),
  });
  const stats = data as StatsResumeCaveSortie | undefined;

  return (
    <Card variant="dark" as="section" className="p-7">
      {isLoading ? (
        <HeroSkeleton />
      ) : isError || !stats ? (
        <HeroMessage>Impossible de charger les échéances de cave pour l&apos;instant.</HeroMessage>
      ) : (
        <HeroContenu stats={stats} />
      )}
    </Card>
  );
}

function HeroContenu({ stats }: { stats: StatsResumeCaveSortie }) {
  // La plus proche des deux échéances réelles (tri par date ISO croissante).
  const candidats: { type: TypeEcheance; echeance: EcheanceCave }[] = [];
  if (stats.prochaine_echeance_degorgement) {
    candidats.push({ type: "degorgement", echeance: stats.prochaine_echeance_degorgement });
  }
  if (stats.prochaine_echeance_mise_en_marche) {
    candidats.push({ type: "mise_en_marche", echeance: stats.prochaine_echeance_mise_en_marche });
  }
  candidats.sort((a, b) => a.echeance.date.localeCompare(b.echeance.date));
  const prochaine = candidats[0] ?? null;

  const { nb_lots_degorgeables: degorgeables, nb_lots_expediables: expediables } = stats;
  const aucunCompteur = degorgeables === 0 && expediables === 0;

  // État vide honnête : ni échéance à venir, ni lot déjà mûr → rien à afficher.
  if (!prochaine && aucunCompteur) {
    return <HeroMessage>Aucune échéance cave à venir.</HeroMessage>;
  }

  // CTA : fiche du lot de l'échéance si présent (route /stock/:id), sinon
  // la liste de stock cave. (`?lot=` n'était pas consommé par la liste.)
  const ctaTo = prochaine ? `/stock/${prochaine.echeance.lot_id}` : "/stock";
  const ctaLabel = prochaine ? "Voir le lot en cave" : "Voir mes lots en cave";

  return (
    <div className="max-w-2xl">
      {prochaine ? (
        <EnTeteEcheance type={prochaine.type} echeance={prochaine.echeance} />
      ) : (
        <EnTeteLotsMurs />
      )}

      {/* Compteurs de lots déjà mûrs — rappel sous l'échéance principale. */}
      {!aucunCompteur && (
        <div className="mt-5 flex flex-wrap gap-2.5">
          {degorgeables > 0 && (
            <CompteurPastille
              nombre={degorgeables}
              singulier="lot dégorgeable maintenant"
              pluriel="lots dégorgeables maintenant"
            />
          )}
          {expediables > 0 && (
            <CompteurPastille
              nombre={expediables}
              singulier="lot commercialisable maintenant"
              pluriel="lots commercialisables maintenant"
            />
          )}
        </div>
      )}

      <div className="mt-6">
        <Link
          to={ctaTo}
          className="cta-flat inline-flex h-11 items-center gap-2 rounded-xl px-4 text-[13px]"
        >
          {ctaLabel}
          <ArrowRight size={16} />
        </Link>
      </div>
    </div>
  );
}

/** En-tête quand une échéance à venir existe : lot + verbe + date + règle. */
function EnTeteEcheance({ type, echeance }: { type: TypeEcheance; echeance: EcheanceCave }) {
  const config = ECHEANCE_CONFIG[type];
  // Libellé du lot absent → repli neutre (on n'expose jamais l'UUID brut).
  const nomLot = echeance.lot_libelle ?? "Un lot";

  return (
    <>
      <BadgeHero libelle={config.badge} />
      <div className="mt-4 text-[22px] leading-tight" style={{ letterSpacing: "-0.01em" }}>
        <span className="font-semibold">{nomLot}</span> {config.verbe}
        <br />à partir du <span className="mono tnum">{formatDateFr(echeance.date)}</span>
      </div>
      <p className="mt-3 text-[13px] text-champagne-50/65">{config.regle}</p>
    </>
  );
}

/** En-tête quand aucune échéance à venir mais des lots sont déjà mûrs. */
function EnTeteLotsMurs() {
  return (
    <>
      <BadgeHero libelle="Cave · lots mûrs" />
      <div className="mt-4 text-[22px] leading-tight" style={{ letterSpacing: "-0.01em" }}>
        <span className="font-semibold">Des lots sont prêts</span> en cave
      </div>
      <p className="mt-3 text-[13px] text-champagne-50/65">
        Aucune nouvelle échéance à venir, mais certains lots ont atteint leur maturité AOC.
      </p>
    </>
  );
}

/** Badge d'en-tête réutilisé : icône calendrier + libellé mono en capitales. */
function BadgeHero({ libelle }: { libelle: string }) {
  return (
    <div className="flex items-center gap-2.5">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-champagne-300/30 bg-champagne-500/[0.18]">
        <CalendarClock size={20} className="text-champagne-300" />
      </div>
      <div className="mono text-[11px] uppercase tracking-[0.14em] text-champagne-300">
        {libelle}
      </div>
    </div>
  );
}

/** Pastille d'un compteur de lots mûrs (accord singulier/pluriel géré). */
function CompteurPastille({
  nombre,
  singulier,
  pluriel,
}: {
  nombre: number;
  singulier: string;
  pluriel: string;
}) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-lg border border-champagne-300/30 bg-champagne-500/[0.14] px-2.5 py-1 text-[12px] text-champagne-100">
      <Wine size={13} className="text-champagne-300" />
      <span className="mono tnum font-semibold">{nombre}</span> {nombre > 1 ? pluriel : singulier}
    </span>
  );
}

/** Message d'état (erreur / vide) aligné sur le style clair du hero sombre. */
function HeroMessage({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center gap-2.5 text-[14px] text-champagne-50/70">
      <CalendarClock size={18} className="text-champagne-300" />
      {children}
    </div>
  );
}

/** Squelette de chargement adapté au fond sombre (Skeleton UI est clair). */
function HeroSkeleton() {
  return (
    <div className="space-y-4" aria-busy="true">
      <div className="h-10 w-10 animate-pulse rounded-lg bg-champagne-300/15" />
      <div className="h-6 w-2/3 animate-pulse rounded-md bg-champagne-300/15" />
      <div className="h-4 w-1/2 animate-pulse rounded-md bg-champagne-300/10" />
      <div className="mt-2 h-11 w-40 animate-pulse rounded-xl bg-champagne-300/15" />
    </div>
  );
}
